/**
 * Attachment routing logic — pure functions for determining
 * how files should be handled (inline, upload, or path reference).
 */
import { isTextCodeExt, MAX_MODEL_MEDIA_BYTES, MAX_MODEL_TEXT_BYTES, MAX_MODEL_TEXT_PER_MESSAGE_BYTES, isModelTextMime } from '@papan/types'
import type { Attachment } from './hooks.js'

/** Max file size that can be inlined as base64 (10MB) */
export const MAX_INLINE_SIZE = MAX_MODEL_MEDIA_BYTES

/** MIME types that can be inlined (images + text) */
const INLINE_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/html',
  'text/css',
  'text/javascript',
  'application/json',
  'application/xml',
])

/** Check if a file can be inlined (image or text, under size limit).
 *
 *  The size guard uses `Number.isFinite` rather than a bare `size >
 *  MAX_INLINE_SIZE`: a missing/NaN size (a drive item whose metadata omits
 *  `size`) makes `undefined > MAX` evaluate `false`, which would sail a
 *  size-unknown file straight past the cap and inline arbitrarily large bytes.
 *  Treat an unknown size as "not inlinable" (fail closed on the cap). */
export function canInline(mime: string, size: number): boolean {
  if (!Number.isFinite(size) || size > MAX_INLINE_SIZE) return false
  // #1292 D4 (option b, chosen at adoption 2026-10-26): text the AI won't read in
  // the message — over MAX_MODEL_TEXT_BYTES, it gets only a "too large" note —
  // is never inlined. Inline, it rode the send for nothing and used up the
  // message's 10 MB budget, pushing a photo the AI COULD see to the upload. A
  // local file goes to the session upload instead (the model is told to move it
  // to the drive to read it); a drive file is referenced by its path, so the
  // agent reads the original in place.
  if (isModelTextMime(mime) && size > MAX_MODEL_TEXT_BYTES) return false
  return INLINE_MIMES.has(mime) || mime.startsWith('text/')
}

/** The MIME types a DRIVE drag inlines: images + PDF (the model sees them as
 *  file parts) and `text/plain`. Since #1291 the model also receives every
 *  other text type (csv/json/html/…) as text, inline or uploaded — but a drive
 *  file needs no copy for that: the path reference lets the agent read the
 *  original in place, so fetching + base64'ing + persisting a second copy of a
 *  drive csv would be wasted work. `canInline` accepts the wider set for local
 *  paste/drop, where there is no original to point at. */
export function isModelInlineDeliverable(mime: string): boolean {
  return mime.startsWith('image/') || mime === 'application/pdf' || mime === 'text/plain'
}

/** Resolve a usable MIME for routing. Browsers report an empty type or
 *  `application/octet-stream` for many source/text files (e.g. Google Apps
 *  Script `.gs`), which sent them to the model as an `application/octet-stream`
 *  file part the provider rejects. When the browser gives us nothing usable AND
 *  the extension names source/text, treat it as `text/plain` so it inlines and
 *  is delivered as text (see `convert-messages.ts`) instead (#657). Genuinely
 *  unknown binaries keep `application/octet-stream` → upload, path-only.
 *
 *  Only fires when the browser gave us NOTHING usable: a `.csv`/`.json` the
 *  browser DID type (`text/csv`/`application/json`) keeps that type and its
 *  existing file-part delivery — so the same extension can reach the model two
 *  ways depending on the browser. That asymmetry is deliberate: this recovers
 *  the unknown-typed case without re-routing files that already work.
 *
 *  Gate on empty/octet-stream ONLY, NOT `isUninformativeMime` — that helper
 *  also treats archives (`application/zip`, …) as uninformative, which for
 *  storage bucketing is right but here is wrong: a real ZIP named `release.js`
 *  must NOT be reclassified to text and inlined as garbage (Codex, #657). An
 *  archive MIME is informative — it says "binary". */
export function resolveMimeType(name: string, type: string): string {
  const t = (type || '').trim().toLowerCase()
  const unknownType = t === '' || t === 'application/octet-stream'
  if (unknownType && isTextCodeExt(name)) return 'text/plain'
  return type || 'application/octet-stream'
}

export type RouteAction =
  | { action: 'inline'; mime: string }
  | { action: 'upload'; mime: string }
  | { action: 'insert-path'; mime: string }
  | { action: 'blocked'; mime: string; reason: string }

/** Determine how to handle a file */
export function routeFile(file: {
  name: string
  type: string
  size: number
  source: 'paste' | 'drop' | 'drive' | 'attach'
  /** Whether the caller can write to the session's storage (upload/inline
   *  bytes). A view-only actor may only reference existing Drive files —
   *  never re-embed or upload new bytes. */
  canWrite: boolean
}): RouteAction {
  const mime = resolveMimeType(file.name, file.type)

  // Drive-sourced files: a WRITER may inline an inlinable image/PDF so the
  // model sees it in-turn (chip + bytes, like the pre-#639 behavior);
  // everything else references by path so the agent reads the original in
  // place via read_file. The canWrite gate is load-bearing — it preserves the
  // #639 fix (a view-only actor must never mint new bytes from a read-only
  // file: it falls through to insert-path) while restoring the writer's
  // instant-vision chip. Non-inlinable or too-large drive files never upload;
  // they always fall through to a path reference (the original already lives
  // in the drive, so there is nothing to copy).
  if (file.source === 'drive') {
    // Inline ONLY what the model receives inline (media + text/plain). A
    // writer dragging a csv/json/html/xml from the drive would otherwise be
    // fetched + base64'd + persisted only for `convert-messages` to breadcrumb
    // it — wasted work when the path reference below already lets the agent
    // read the original in place.
    if (file.canWrite && isModelInlineDeliverable(mime) && canInline(mime, file.size)) {
      return { action: 'inline', mime }
    }
    return { action: 'insert-path', mime }
  }

  // View-only: no new bytes may be created from paste/drop/local/attach.
  if (!file.canWrite) {
    return { action: 'blocked', mime, reason: 'view-only: link existing files' }
  }

  // Inlinable files under size limit
  if (canInline(mime, file.size)) {
    return { action: 'inline', mime }
  }

  // Everything else: upload to the session-private CAS store
  // (uploadSessionAttachment, #639 Task 3.4) — never the drive.
  return { action: 'upload', mime }
}

// #1291/#1292: what the AI is shown — ONE definition with the server
// (`@papan/types` chat-attachments.ts), so the notice below never disagrees
// with what convert-messages actually sends the model.
export { MAX_MODEL_MEDIA_BYTES, MAX_MODEL_TEXT_BYTES, isModelTextMime }

/**
 * #1292: what to tell the user when a file they attach will be ATTACHED but not
 * SEEN by the AI — the model gets only a note in its place, and without this the
 * user believes it looked and gets an answer built from their text alone.
 * `undefined` when the AI sees it. Measured on the raw size, before any route.
 */
export function aiVisibilityNotice(mime: string, size: number): AiNotice | undefined {
  const media = mime.startsWith('image/') || mime === 'application/pdf'
  // Any other type that isn't text (a .docx, .xlsx, .zip) reaches the model only
  // as a note telling it to move the file to the drive first (convert-messages
  // `notViewableNote`) — the same "attached, not seen" as an oversized file.
  if (!media && !isModelTextMime(mime)) return 'not-viewable'
  if (!Number.isFinite(size)) return undefined
  if (media && size > MAX_MODEL_MEDIA_BYTES) return 'media-too-large'
  if (isModelTextMime(mime) && size > MAX_MODEL_TEXT_BYTES) return 'text-too-large'
  return undefined
}

/** Which notice, kept as a CODE on the attachment and worded at render time
 *  (`aiNoticeMessage` with the component's `t`), so it follows a language switch.
 *  `text-over-message-total` is never stored: it depends on the other chips, so
 *  `messageTextBudgetNotices` derives it from the list at render. */
export type AiNotice = 'media-too-large' | 'text-too-large' | 'not-viewable' | 'text-over-message-total'

/**
 * The text chips the AI won't read because the files BEFORE them in this message
 * already bring `MAX_MODEL_TEXT_PER_MESSAGE_BYTES` of text — the composer half of
 * convert-messages' per-message text budget (same number, same order: the list's).
 * A chip already flagged (too large on its own, not viewable) uses none of it, as
 * on the server; a moved chip (`fileId`) is read by path, not folded in; a chip
 * with neither bytes nor a handle (a drive drop whose fetch failed, now a path
 * reference) brings no text. An edit's chip from an earlier message is sized from
 * its inline bytes; an uploaded one from an earlier message, whose size isn't
 * known here, is skipped — the server still applies the budget to it.
 */
export function messageTextBudgetNotices(
  list: ReadonlyArray<Pick<Attachment, 'id' | 'mime' | 'status' | 'aiNotice' | 'fileId' | 'textBytes' | 'inlineBytes' | 'url' | 'handle'>>,
): Map<string, AiNotice> {
  const out = new Map<string, AiNotice>()
  let left = MAX_MODEL_TEXT_PER_MESSAGE_BYTES
  for (const a of list) {
    if (!isModelTextMime(a.mime) || a.status === 'error' || a.aiNotice || a.fileId) continue
    if (a.status === 'ready' && !a.url && !a.handle) continue // a finished path-only chip; one still uploading counts
    const bytes = a.textBytes ?? a.inlineBytes ?? dataUrlBytes(a.url)
    if (bytes === undefined) continue
    if (bytes > left) out.set(a.id, 'text-over-message-total')
    else left -= bytes
  }
  return out
}

/** The raw size of a `data:` URL's payload, without decoding it; `undefined` when
 *  it isn't one. */
export function dataUrlBytes(url: string | undefined): number | undefined {
  if (!url || !url.startsWith('data:')) return undefined
  const comma = url.indexOf(',')
  if (comma < 0) return 0
  const seg = url.slice(comma + 1)
  if (url.slice(5, comma).includes('base64')) {
    const pad = seg.endsWith('==') ? 2 : seg.endsWith('=') ? 1 : 0
    return Math.max(0, Math.floor(seg.length * 3 / 4) - pad)
  }
  try { return new TextEncoder().encode(decodeURIComponent(seg)).length } catch { return seg.length }
}

export function aiNoticeMessage(code: AiNotice, t: (key: string, opts?: Record<string, unknown>) => string): string {
  switch (code) {
    case 'media-too-large': return t('attachmentNotices.aiWontSeeMedia', { max: MAX_MODEL_MEDIA_BYTES / (1024 * 1024) })
    case 'text-too-large': return t('attachmentNotices.aiCantReadText', { max: MAX_MODEL_TEXT_BYTES / 1024 })
    case 'not-viewable': return t('attachmentNotices.aiCantOpenType')
    case 'text-over-message-total': return t('attachmentNotices.aiCantReadTextTotal', { max: MAX_MODEL_TEXT_PER_MESSAGE_BYTES / 1024 })
    default: {
      const unknown: never = code
      return String(unknown)
    }
  }
}

/** #1292: the route for a file whose inline route would pass the MESSAGE's
 *  10 MB budget (`inlineBudget.ts`). A local file goes to the session upload —
 *  the AI still sees an image or PDF from there. A drive file is referenced by
 *  its path, exactly as a drive file too large to inline already is (the
 *  original is in the drive; nothing to copy). */
export function overflowRoute(source: 'paste' | 'drop' | 'drive' | 'attach', mime: string): RouteAction {
  return source === 'drive' ? { action: 'insert-path', mime } : { action: 'upload', mime }
}

let counter = 0

/** Generate a unique attachment ID */
export function createAttachmentId(): string {
  return `att_${Date.now()}_${++counter}`
}

/** Read a File as a base64 data URL */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/** Detect whether a PDF is password-protected without fully parsing it.
 *
 *  An encrypted PDF has an `/Encrypt` entry in its trailer dictionary.
 *  The trailer lives near the end of the file, so we only scan the last
 *  64 KB. Reading the whole file first would be wasteful for large PDFs,
 *  and string-matching is fine here: `/Encrypt` is a PDF keyword that
 *  doesn't appear in compressed content streams.
 *
 *  Used in the attachment pipeline to avoid sending encrypted bytes to
 *  the LLM — providers reject them with `invalid_file` and crash the turn.
 *  We skip the inline preview and let the file flow through as a path-only
 *  reference, so the agent can call cred_to_env + pymupdf instead. */
export async function isEncryptedPdf(file: File): Promise<boolean> {
  if (file.size < 5) return false
  const head = new Uint8Array(await file.slice(0, 5).arrayBuffer())
  // %PDF- = 0x25 50 44 46 2D
  if (head[0] !== 0x25 || head[1] !== 0x50 || head[2] !== 0x44 || head[3] !== 0x46 || head[4] !== 0x2D) {
    return false
  }
  const tailStart = Math.max(0, file.size - 65536)
  const tail = await file.slice(tailStart).text()
  return tail.includes('/Encrypt')
}

/**
 * Re-point a COMPOSER (pre-send) attachment at the drive file it was just
 * moved into (#662 Task 8). A sent chip's move is re-pointed server-side
 * (`repointAttachmentPartsToDrive`, matched by a persisted message-part id);
 * a composer attachment has no persisted part yet, so the client is the only
 * place this can happen — without it, the moved attachment stays inline (or
 * handle-bound) and the SENT message never becomes a drive reference (the
 * bug this task fixes).
 *
 * Mirrors `repointAttachmentPartsToDrive`'s own rewrite exactly: `fileId` +
 * `path` set, `url` cleared, `handle` dropped, `source: 'drive'` — so the
 * outgoing `file` part (hooks.ts's `sendMessage`) is indistinguishable from
 * a drive-dragged "insert-path" attachment, and the chip renders the same
 * persistent "In the drive" tick `AttachmentChip` derives from `fileId`.
 *
 * `url: undefined` rather than `''`: `Attachment.url` is optional, and the
 * chip treats any falsy `url` as "no inline bytes" — `undefined` reads as
 * cleanly as `''` there but also drops the key when the attachment object is
 * spread into a JSON body later (no `"url":""` clutter on the wire).
 */
export function markAttachmentMoved(att: Attachment, drive: { fileId: string; path: string }): Attachment {
  // #758 (boss 2026-10-09): the move no longer stashes the bytes. #662 kept
  // the original base64 in `previewUrl` so that deleting the drive file could
  // revert the chip to a clickable inline preview — at the cost of a second
  // physical copy that lived forever. Once inline bytes started counting
  // (0299) that made the page's own "moving frees this quota" false: the
  // drive figure rose and the chat figure did not move. The stash only ever
  // paid off for a member who moved a file to the Drive and then deleted it
  // there, which says twice that they do not want it.
  //
  // An attachment that ALREADY carries a stash keeps it (the decision covers
  // future moves), so this clears `previewUrl` only where it would have just
  // written one. The server does the same in `repointAttachmentPartsToDrive`,
  // which is also where the ledger row is dropped — this function is pure and
  // only shapes the chip.
  const hadInlineBytes = !!(att.url && att.url.startsWith('data:'))
  const previewUrl = hadInlineBytes ? undefined : att.previewUrl
  return { ...att, fileId: drive.fileId, path: drive.path, previewUrl, url: undefined, handle: undefined, source: 'drive' }
}
